import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';
import hasRole from '../utils/hasRole';
import { saveAppearance } from '../theme/saveAppearance';

const AuthContext = createContext(null);
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.me();
      setUser(data.data);
    } catch {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (credentials, isCustomer = false) => {
    const { data } = isCustomer
      ? await authAPI.customerLogin(credentials)
      : await authAPI.login(credentials);

    localStorage.setItem('token', data.data.token);
    if (data.data.refreshToken) localStorage.setItem('refreshToken', data.data.refreshToken);
    setUser(data.data.user);
    // The login response is a slim payload (no populated siteRole) — refresh from /me
    // right away so hasModuleRight has real rights to check instead of waiting for the
    // next full page reload to pick them up.
    loadUser();
    return data.data;
  };

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    // Full reload so the next login runs the latest deployed build — an in-page logout/login
    // (e.g. the 5-minute idle logout) kept a tab on the morning's old code all day.
    window.location.reload();
  }, []);

  const setAppearance = useCallback((appearance) => saveAppearance({
    value: appearance,
    previous: user?.appearance,
    update: (v) => setUser(u => (u ? { ...u, appearance: v } : u)),
    persist: (v) => authAPI.setAppearance(v),
  }), [user?.appearance]);

  // Auto-logout after 5 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(logout, INACTIVITY_TIMEOUT);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timerRef.current);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, logout]);

  const isAdmin = user?.role === 'admin'; // unchanged: admin is never a secondary role
  const isStaff = hasRole(user, 'staff');
  const isCoAdmin = hasRole(user, 'co-admin');
  const isCustomer = user?.role === 'customer'; // unchanged: customer is never a secondary role
  const isTechnical = hasRole(user, 'technical');
  const isStaffOrAdmin = isAdmin || isStaff || isCoAdmin;
  // Network-hardware management actions (OLT/fiber reboot, provision, delete, label edit)
  // are gated to admin or the technical login — staff stays view-only there, same as before.
  const canManageHardware = isAdmin || isTechnical;

  // Site Roles matrix (see config/moduleRegistry.js on the backend) — admin bypasses
  // everything here too, so call sites don't need to repeat `isAdmin ||` themselves.
  // Reads off the populated `siteRole.rights` (see middleware/auth.js's protect, which
  // populates siteRole on every request) — NOT the deprecated flat `user.moduleRights`
  // field, which nothing writes to anymore and would make this always false for staff.
  const hasModuleRight = (moduleKey, action = 'view') =>
    isAdmin || !!user?.siteRole?.rights?.[moduleKey]?.[action];

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isStaff, isCoAdmin, isCustomer, isTechnical, isStaffOrAdmin, canManageHardware, hasModuleRight, loadUser, setAppearance }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
